import {
	Badge,
	Button,
	Card,
	CardContent,
	CardHeader,
	CardTitle,
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuTrigger,
	ResponsiveTable,
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
	Skeleton,
} from "@ancatag/at-design";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { MoreVertical, XCircle } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { sessionsService } from "../../services/admin";

export default function AdminSessions() {
	const [page, setPage] = useState(1);
	const [limit] = useState(50);
	const [filters, setFilters] = useState<{
		userId?: string;
		status?: "active" | "revoked";
	}>({});

	const queryClient = useQueryClient();

	const { data: sessionsData, isLoading } = useQuery({
		queryKey: ["admin-sessions", page, limit, filters],
		queryFn: () => sessionsService.getSessions(page, limit, filters),
	});

	const revokeMutation = useMutation({
		mutationFn: (sessionId: string) => sessionsService.revokeSession(sessionId),
		onSuccess: () => {
			toast.success("Session revoked successfully");
			queryClient.invalidateQueries({ queryKey: ["admin-sessions"] });
		},
		onError: (error: any) => {
			toast.error(error.message || "Failed to revoke session");
		},
	});

	const sessions = sessionsData?.data || [];
	const meta = sessionsData?.meta;

	const handleRevoke = (sessionId: string) => {
		if (confirm("Are you sure you want to revoke this session?")) {
			revokeMutation.mutate(sessionId);
		}
	};

	return (
		<div className="space-y-6">
			{/* Header */}
			<div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
				<div>
					<h1 className="text-2xl md:text-3xl font-bold text-foreground">
						Session Management
					</h1>
					<p className="text-muted-foreground mt-2">
						Monitor and revoke user sessions
					</p>
				</div>
			</div>

			{/* Filters */}
			<Card>
				<CardHeader>
					<CardTitle className="text-lg">Filters</CardTitle>
				</CardHeader>
				<CardContent>
					<div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
						<div className="space-y-2">
							<label className="text-xs text-muted-foreground">Status</label>
							<Select
								value={filters.status || "all"}
								onValueChange={(value) => {
									setFilters({
										...filters,
										status: value === "all" ? undefined : (value as any),
									});
									setPage(1);
								}}
							>
								<SelectTrigger>
									<SelectValue placeholder="All Statuses" />
								</SelectTrigger>
								<SelectContent>
									<SelectItem value="all">All Statuses</SelectItem>
									<SelectItem value="active">Active</SelectItem>
									<SelectItem value="revoked">Revoked</SelectItem>
								</SelectContent>
							</Select>
						</div>
					</div>
				</CardContent>
			</Card>

			{/* Sessions Table */}
			<Card>
				<CardHeader>
					<CardTitle className="text-lg">
						Sessions {meta && `(${meta.total.toLocaleString()} total)`}
					</CardTitle>
				</CardHeader>
				<CardContent>
					{isLoading ? (
						<SessionsTableSkeleton />
					) : (
						<>
							<ResponsiveTable
								data={sessions}
								keyExtractor={(session) => session.id}
								columns={[
									{
										key: "user",
										header: "User",
										mobileLabel: "User",
										render: (session) => (
											<div>
												<p className="text-sm font-medium">
													{session.user?.email || "Unknown"}
												</p>
												<p className="text-xs text-muted-foreground">
													{session.user?.name || "—"}
												</p>
											</div>
										),
									},
									{
										key: "device",
										header: "Device",
										mobileLabel: "Device",
										render: (session) => (
											<span className="text-sm">
												{session.deviceName || session.deviceId || "Unknown"}
											</span>
										),
									},
									{
										key: "ipAddress",
										header: "IP Address",
										mobileLabel: "IP",
										hideOnMobile: true,
										render: (session) => (
											<span className="text-sm font-mono">
												{session.ipAddress || "—"}
											</span>
										),
									},
									{
										key: "status",
										header: "Status",
										mobileLabel: "Status",
										render: (session) => (
											<Badge
												variant={session.isRevoked ? "secondary" : "default"}
												className={
													session.isRevoked
														? "bg-red-500/20 text-red-600 border-red-500/30"
														: "bg-green-500/20 text-green-600 border-green-500/30"
												}
											>
												{session.isRevoked ? "Revoked" : "Active"}
											</Badge>
										),
									},
									{
										key: "created",
										header: "Created",
										mobileLabel: "Created",
										hideOnMobile: true,
										render: (session) => (
											<span className="text-sm">
												{new Date(session.createdAt).toLocaleDateString()}
											</span>
										),
									},
									{
										key: "lastUsed",
										header: "Last Used",
										mobileLabel: "Last Used",
										hideOnMobile: true,
										render: (session) => (
											<span className="text-sm">
												{session.lastUsedAt
													? new Date(session.lastUsedAt).toLocaleDateString()
													: "Never"}
											</span>
										),
									},
									{
										key: "expires",
										header: "Expires",
										mobileLabel: "Expires",
										hideOnMobile: true,
										render: (session) => (
											<span className="text-sm">
												{new Date(session.expiresAt).toLocaleDateString()}
											</span>
										),
									},
									{
										key: "actions",
										header: "Actions",
										mobileLabel: "Actions",
										render: (session) => (
											<DropdownMenu>
												<DropdownMenuTrigger asChild>
													<Button variant="ghost" size="icon">
														<MoreVertical className="h-4 w-4" />
													</Button>
												</DropdownMenuTrigger>
												<DropdownMenuContent align="end">
													{!session.isRevoked && (
														<DropdownMenuItem
															onClick={() => handleRevoke(session.id)}
														>
															<XCircle className="h-4 w-4 mr-2" />
															Revoke Session
														</DropdownMenuItem>
													)}
												</DropdownMenuContent>
											</DropdownMenu>
										),
									},
								]}
								emptyMessage="No sessions found matching your filters."
							/>

							{/* Pagination */}
							{meta && meta.totalPages > 1 && (
								<div className="flex items-center justify-between mt-4">
									<div className="text-sm text-muted-foreground">
										Page {meta.page} of {meta.totalPages}
									</div>
									<div className="flex items-center gap-2">
										<Button
											variant="outline"
											size="sm"
											onClick={() => setPage((p) => Math.max(1, p - 1))}
											disabled={!meta.hasPrev}
										>
											Previous
										</Button>
										<Button
											variant="outline"
											size="sm"
											onClick={() =>
												setPage((p) => Math.min(meta.totalPages, p + 1))
											}
											disabled={!meta.hasNext}
										>
											Next
										</Button>
									</div>
								</div>
							)}
						</>
					)}
				</CardContent>
			</Card>
		</div>
	);
}

function SessionsTableSkeleton() {
	return (
		<div className="space-y-2">
			{[1, 2, 3, 4, 5].map((i) => (
				<Skeleton key={i} className="h-12 w-full" />
			))}
		</div>
	);
}
